import { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { FileSpreadsheet, Upload, Loader2, CheckCircle2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { fmtTL, fmtDate } from "@/lib/utils";
import { BackupPanel } from "@/components/BackupPanel";
import { CompanySettings } from "@/components/CompanySettings";

const COLUMNS = ["PROJEKODU (Firma)", "GRUPADI (Proje)", "POZNO", "TANIMI", "CBIRIM", "BFIYAT", "BUHAKMIKTOP", "HAKMIKTOP", "BUIMTUT", "TOPHAKTUT", "HDUZENTARIHI"];

export default function DataImport() {
  const ref = useRef(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm("Önceki Excel aktarımından gelen veriler silinip yeniden yüklenecek. Devam edilsin mi?")) { ref.current.value = ""; return; }
    setLoading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/import/hakedis", fd, { timeout: 300000 });
      setResult(r.data);
      toast.success("Hakediş verileri aktarıldı");
    } catch (err) {
      toast.error("Aktarım başarısız: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
      ref.current.value = "";
    }
  };

  return (
    <div data-testid="import-page">
      <PageHeader title="Veri Aktarımı" subtitle="Hakediş detay raporunu (Excel) yükleyerek projeleri, firmaları ve iş kalemlerini oluşturun." />
      <div className="rounded-xl border-2 border-dashed border-amber-500/40 bg-amber-500/5 p-10 text-center max-w-3xl">
        <FileSpreadsheet className="w-12 h-12 mx-auto text-amber-600 mb-3" />
        <div className="font-display font-bold text-lg">Hakediş Detay Raporu (.xlsx)</div>
        <p className="text-sm text-muted-foreground mt-1 mb-5">Dosyadaki her satır bir hakediş kalemi olarak işlenir. Firma + tarih kombinasyonu bir hakediş belgesi oluşturur.</p>
        <input type="file" accept=".xlsx,.xlsm" ref={ref} className="hidden" onChange={onFile} data-testid="import-file-input" />
        <Button onClick={() => ref.current?.click()} disabled={loading} className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold" data-testid="import-button">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
          {loading ? "Aktarılıyor..." : "Excel Dosyası Seç"}
        </Button>
        <div className="mt-6 text-xs text-muted-foreground">
          Beklenen sütunlar: {COLUMNS.map(c => <code key={c} className="mx-0.5 px-1.5 py-0.5 rounded bg-muted">{c}</code>)}
        </div>
      </div>
      {result && (
        <div className="mt-6 rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-6 max-w-3xl" data-testid="import-result">
          <div className="flex items-center gap-2 font-semibold text-emerald-700 dark:text-emerald-400 mb-4"><CheckCircle2 className="w-5 h-5" /> Aktarım tamamlandı</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <Stat label="Proje" value={result.projects} /><Stat label="Firma" value={result.firms} /><Stat label="Hakediş" value={result.payments} />
            <Stat label="İş Kalemi" value={result.work_items} /><Stat label="Hakediş Satırı" value={result.lines} /><Stat label="Toplam Tutar" value={fmtTL(result.total_amount)} />
          </div>
          <div className="text-xs text-muted-foreground mt-3">Dönem: {fmtDate(result.date_from)} — {fmtDate(result.date_to)}</div>
        </div>
      )}
      <CompanySettings />
      <BackupPanel />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-card border border-border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-mono-nums font-bold">{value}</div>
    </div>
  );
}
